import {
  collection,
  doc,
  runTransaction,
  serverTimestamp,
  writeBatch,
  updateDoc,
  query,
  where,
  deleteDoc,
  getDoc,
  getDocs,
} from "firebase/firestore";
import { format } from "date-fns";
import { db } from "../lib/firebase";
import { CartItem, MealType } from "../types/index";
import { call } from "./callables";

export interface SubscriptionConfig {
  durationDays: number;
  startDate: Date;
  meals: MealType[];
  totalAmount: number;
  lunchTime?: "8AM" | "12PM";
  lunchLocation?: "home" | "office";
  /** "scheduled" when the customer picked a future start date (activateSubscriptions flips it at midnight). */
  status?: "active" | "scheduled";
}

/** Local (IST in the browser) calendar key. toISOString() is UTC and gives the previous day before 05:30 IST. */
export const localDateKey = (d: Date) => format(d, "yyyy-MM-dd");

export interface PlanDroppedItem {
  date: string;
  meal: MealType;
  id: string;
  name: string;
  reason: string;
  backup?: boolean;
}

export interface PlanResult {
  dryRun?: boolean;
  subscriptionId?: string;
  status: "active" | "scheduled";
  startDate: string; // YYYY-MM-DD (IST)
  endDate: string;   // first day after the plan
  lastDay: string;   // last day with meals
  totalAmount: number; // priced by the server from the kitchen menus
  days: number;
  dropped: PlanDroppedItem[];
  renewalOf?: string;
  previousEndDate?: string;
}

/** Only what the server needs: it looks up name / price / availability on the kitchen's menu itself. */
const toServerSelections = (calendarSelections: Record<string, Record<string, CartItem[]>>) => {
  const out: Record<string, Record<string, { id: string; kitchenId: string; quantity: number; customizations?: any }[]>> = {};
  Object.entries(calendarSelections || {}).forEach(([dateKey, perMeal]) => {
    Object.entries(perMeal || {}).forEach(([meal, items]) => {
      const picks = (items || [])
        .slice(0, 2)
        .filter((it) => it && it.id && it.kitchenId)
        .map((it) => ({
          id: String(it.id),
          kitchenId: String(it.kitchenId),
          quantity: Number(it.quantity) || 1,
          ...(Array.isArray(it.customizations)
            ? { customizations: (it.customizations as any[]).filter((c) => typeof c?.label === "string" && typeof c?.value === "string") }
            : {}),
        }));
      if (picks.length) (out[dateKey] = out[dateKey] || {})[meal] = picks;
    });
  });
  return out;
};

const planPayload = (config: SubscriptionConfig, calendarSelections: Record<string, Record<string, CartItem[]>>) => ({
  startDate: localDateKey(new Date(config.startDate)),
  durationDays: config.durationDays,
  meals: config.meals ?? [],
  lunchTime: config.lunchTime ?? "12PM",
  lunchLocation: config.lunchLocation ?? null,
  selections: toServerSelections(calendarSelections),
});

/** Server quote for a plan (prices from the kitchen menus; nothing is written). */
export const quoteSubscriptionPlan = (
  config: SubscriptionConfig,
  calendarSelections: Record<string, Record<string, CartItem[]>>
) => call<PlanResult>("createSubscriptionPlan", { ...planPayload(config, calendarSelections), dryRun: true });

/**
 * Create a subscription with daily meal plan.
 * Goes through the createSubscriptionPlan callable, which re-prices every item from the kitchen's
 * menu on the server (client prices are ignored) and writes the subscription + daily tiffins.
 * Returns the new subscription id.
 */
export const createSubscriptionWithPlan = async (
  _userId: string,
  config: SubscriptionConfig,
  calendarSelections: Record<string, Record<string, CartItem[]>>
): Promise<string> => {
  const res = await createSubscriptionPlan(config, calendarSelections);
  return res.subscriptionId!;
};

export const createSubscriptionPlan = (
  config: SubscriptionConfig,
  calendarSelections: Record<string, Record<string, CartItem[]>>
) => call<PlanResult>("createSubscriptionPlan", planPayload(config, calendarSelections));

/**
 * Renew a plan: same items, meals and days, same duration, starting the day after it ends
 * (tomorrow if it already ended), prices recomputed from the menus. Same server path as signup.
 */
export const renewSubscription = (subscriptionId: string, dryRun = false) =>
  call<PlanResult>("renewSubscription", { subscriptionId, dryRun });

/**
 * Add a special item to an existing subscription
 */
export const addSpecialToSubscription = async (
  subscriptionId: string,
  specialItem: CartItem,
  date?: string
) => {
  try {
    const subRef = doc(db, "subscriptions", subscriptionId);
    const specialRef = doc(collection(subRef, "specials"));

    await runTransaction(db, async (transaction) => {
      transaction.update(subRef, {
        updatedAt: serverTimestamp(),
      });

      transaction.set(specialRef, {
        item: specialItem,
        date: date || null,
        addedAt: serverTimestamp(),
        status: "active",
      });
    });

    return specialRef.id;
  } catch (error) {
    console.error("Error adding special to subscription:", error);
    throw error;
  }
};

/**
 * Remove a special item from a subscription
 */
export const removeSpecialFromSubscription = async (
  subscriptionId: string,
  specialId: string
) => {
  try {
    const subRef = doc(db, "subscriptions", subscriptionId);
    const specialRef = doc(subRef, "specials", specialId);

    await runTransaction(db, async (transaction) => {
      const specialDoc = await transaction.get(specialRef);

      if (specialDoc.exists()) {
        transaction.delete(specialRef);
        transaction.update(subRef, {
          updatedAt: serverTimestamp(),
        });
      }
    });
  } catch (error) {
    console.error("Error removing special from subscription:", error);
    throw error;
  }
};

/**
 * Pause a subscription
 */
export const pauseSubscription = async (subscriptionId: string) => {
  try {
    const subRef = doc(db, "subscriptions", subscriptionId);
    await updateDoc(subRef, {
      status: "paused",
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error pausing subscription:", error);
    throw error;
  }
};

/**
 * Resume a paused subscription
 */
export const resumeSubscription = async (subscriptionId: string) => {
  try {
    const subRef = doc(db, "subscriptions", subscriptionId);
    await updateDoc(subRef, {
      status: "active",
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error resuming subscription:", error);
    throw error;
  }
};

/**
 * Cancel a subscription
 */
export const cancelSubscription = async (subscriptionId: string) => {
  try {
    const subRef = doc(db, "subscriptions", subscriptionId);
    await updateDoc(subRef, {
      status: "cancelled",
      updatedAt: serverTimestamp(),
    });
    // Cancel the upcoming days too, so they stop showing as "scheduled" (the meal-lock cron also refuses
    // to cook or charge tiffins of a cancelled subscription).
    const todayKey = localDateKey(new Date());
    const snap = await getDocs(
      query(
        collection(db, "daily_tiffins"),
        where("subscriptionId", "==", subscriptionId),
        where("status", "==", "scheduled")
      )
    );
    const upcoming = snap.docs.filter((d) => String(d.data()?.date || "") >= todayKey);
    for (let i = 0; i < upcoming.length; i += 400) {
      const b = writeBatch(db);
      upcoming.slice(i, i + 400).forEach((d) =>
        b.update(d.ref, { status: "cancelled", skipReason: "Subscription cancelled", updatedAt: serverTimestamp() })
      );
      await b.commit();
    }
  } catch (error) {
    console.error("Error cancelling subscription:", error);
    throw error;
  }
};

/**
 * Skip a specific day in the subscription
 */
export const skipTiffinDay = async (tiffinId: string, reason?: string) => {
  try {
    const tiffinRef = doc(db, "daily_tiffins", tiffinId);
    await updateDoc(tiffinRef, {
      status: "skipped",
      skipReason: reason || "User skipped",
      skippedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error skipping tiffin day:", error);
    throw error;
  }
};

/**
 * Unskip a day (restore it)
 */
export const unskipTiffinDay = async (tiffinId: string) => {
  try {
    const tiffinRef = doc(db, "daily_tiffins", tiffinId);
    await updateDoc(tiffinRef, {
      status: "scheduled",
      skipReason: null,
      skippedAt: null,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error unskipping tiffin day:", error);
    throw error;
  }
};

/**
 * Skip multiple days at once
 */
export const skipMultipleDays = async (
  tiffinIds: string[],
  reason?: string
) => {
  try {
    const batch = writeBatch(db);

    tiffinIds.forEach((tiffinId) => {
      const tiffinRef = doc(db, "daily_tiffins", tiffinId);
      batch.update(tiffinRef, {
        status: "skipped",
        skipReason: reason || "Bulk skip",
        skippedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    });

    await batch.commit();
  } catch (error) {
    console.error("Error skipping multiple days:", error);
    throw error;
  }
};

/**
 * Get skip count for a subscription
 */
export const getSkipCount = async (
  subscriptionId: string
): Promise<number> => {
  try {
    const q = query(
      collection(db, "daily_tiffins"),
      where("subscriptionId", "==", subscriptionId),
      where("status", "==", "skipped")
    );

    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error("Error getting skip count:", error);
    return 0;
  }
};

export interface BlockedDate {
  id?: string;
  date: string; // YYYY-MM-DD
  fullDate: Date;
  reason: string;
  blockedBy: string; // Admin UID
  type: "platform" | "kitchen";
  kitchenId?: string;
  createdAt: any;
}

export const scheduleSubscriptionStart = async (
  subscriptionId: string,
  startDate: Date
) => {
  try {
    const subRef = doc(db, "subscriptions", subscriptionId);
    await updateDoc(subRef, {
      status: "scheduled",
      startDate,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error scheduling subscription start:", error);
    throw error;
  }
};

export const activateScheduledSubscriptions = async () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const q = query(
    collection(db, "subscriptions"),
    where("status", "==", "scheduled"),
    where("startDate", "<=", today)
  );

  const snapshot = await getDocs(q);
  const batch = writeBatch(db);

  snapshot.forEach((d) => {
    batch.update(d.ref, {
      status: "active",
      updatedAt: serverTimestamp(),
    });
  });

  await batch.commit();
};

export const addBlockedDate = async (
  date: Date,
  reason: string,
  adminUid: string,
  type: "platform" | "kitchen" = "platform",
  kitchenId?: string
) => {
  try {
    const blockedRef = doc(collection(db, "blocked_dates"));
    const dateKey = localDateKey(date);

    await writeBatch(db)
      .set(blockedRef, {
        date: dateKey,
        fullDate: date,
        reason,
        blockedBy: adminUid,
        type,
        kitchenId: kitchenId || null,
        createdAt: serverTimestamp(),
      })
      .commit();

    return blockedRef.id;
  } catch (error) {
    console.error("Error adding blocked date:", error);
    throw error;
  }
};

export const removeBlockedDate = async (blockedDateId: string) => {
  try {
    const blockedRef = doc(db, "blocked_dates", blockedDateId);
    await deleteDoc(blockedRef);
  } catch (error) {
    console.error("Error removing blocked date:", error);
    throw error;
  }
};

export const getBlockedDatesInRange = async (
  startDate: Date,
  endDate: Date,
  kitchenId?: string
): Promise<BlockedDate[]> => {
  try {
    const startKey = localDateKey(startDate);
    const endKey = localDateKey(endDate);

    let q = query(
      collection(db, "blocked_dates"),
      where("date", ">=", startKey),
      where("date", "<=", endKey)
    );

    const snapshot = await getDocs(q);
    const blocked: BlockedDate[] = [];

    snapshot.forEach((d) => {
      const data = d.data() as BlockedDate;
      if (
        data.type === "platform" ||
        (kitchenId &&
          data.type === "kitchen" &&
          data.kitchenId === kitchenId)
      ) {
        blocked.push({ id: d.id, ...data });
      }
    });

    return blocked;
  } catch (error) {
    console.error("Error getting blocked dates:", error);
    return [];
  }
};

export const autoSkipBlockedDates = async () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const blockedDates = await getBlockedDatesInRange(today, tomorrow);

  for (const blocked of blockedDates) {
    const q = query(
      collection(db, "daily_tiffins"),
      where("date", "==", blocked.date),
      where("status", "==", "scheduled")
    );

    const snapshot = await getDocs(q);
    const batch = writeBatch(db);

    snapshot.forEach((d) => {
      const tiffin = d.data();
      if (
        blocked.type === "platform" ||
        tiffin.items?.some(
          (item: CartItem) => item.kitchenId === blocked.kitchenId
        )
      ) {
        batch.update(d.ref, {
          status: "skipped",
          skipReason: `Admin blocked: ${blocked.reason}`,
          skippedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
    });

    await batch.commit();
  }
};

export const addChefBlockedDate = async (
  chefUid: string,
  kitchenId: string,
  date: Date,
  reason: string
) => {
  try {
    const blockedRef = doc(collection(db, "chef_blocked_dates"));
    const dateKey = localDateKey(date);

    await writeBatch(db)
      .set(blockedRef, {
        chefUid,
        kitchenId,
        date: dateKey,
        fullDate: date,
        reason,
        createdAt: serverTimestamp(),
      })
      .commit();

    await autoSkipChefBlockedDate(kitchenId, dateKey, reason);

    return blockedRef.id;
  } catch (error) {
    console.error("Error adding chef blocked date:", error);
    throw error;
  }
};

export const removeChefBlockedDate = async (blockedDateId: string) => {
  try {
    const blockedRef = doc(db, "chef_blocked_dates", blockedDateId);
    const blockedDoc = await getDoc(blockedRef);

    if (blockedDoc.exists()) {
      const { kitchenId, date } = blockedDoc.data() as any;

      const q = query(
        collection(db, "daily_tiffins"),
        where("date", "==", date),
        where("status", "==", "skipped")
      );

      const snapshot = await getDocs(q);
      const batch = writeBatch(db);

      snapshot.forEach((d) => {
        const tiffin = d.data() as any;
        if (
          tiffin.items?.some(
            (item: CartItem) => item.kitchenId === kitchenId
          ) &&
          tiffin.skipReason?.includes("Chef blocked")
        ) {
          batch.update(d.ref, {
            status: "scheduled",
            skipReason: null,
            skippedAt: null,
            updatedAt: serverTimestamp(),
          });
        }
      });

      await batch.commit();
      await deleteDoc(blockedRef);
    }
  } catch (error) {
    console.error("Error removing chef blocked date:", error);
    throw error;
  }
};

const autoSkipChefBlockedDate = async (
  kitchenId: string,
  dateKey: string,
  reason: string
) => {
  const q = query(
    collection(db, "daily_tiffins"),
    where("date", "==", dateKey),
    where("status", "==", "scheduled")
  );

  const snapshot = await getDocs(q);
  const batch = writeBatch(db);

  snapshot.forEach((d) => {
    const tiffin = d.data() as any;
    if (
      tiffin.items?.some(
        (item: CartItem) => item.kitchenId === kitchenId
      )
    ) {
      batch.update(d.ref, {
        status: "skipped",
        skipReason: `Chef blocked: ${reason}`,
        skippedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }
  });

  await batch.commit();
};

export const getChefBlockedDates = async (
  chefUid: string,
  kitchenId: string,
  startDate?: Date,
  endDate?: Date
): Promise<BlockedDate[]> => {
  try {
    let q = query(
      collection(db, "chef_blocked_dates"),
      where("kitchenId", "==", kitchenId)
    );

    if (startDate && endDate) {
      const startKey = localDateKey(startDate);
      const endKey = localDateKey(endDate);

      q = query(
        q,
        where("date", ">=", startKey),
        where("date", "<=", endKey)
      );
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(
      (d) => ({ id: d.id, ...(d.data() as any) } as BlockedDate)
    );
  } catch (error) {
    console.error("Error getting chef blocked dates:", error);
    return [];
  }
};
